import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Avatar, 
  Grid, 
  Card,
  CardContent,
  Divider,
  Chip,
  alpha,
  useTheme,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  TextField,
  Button,
  Badge,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Person as PersonIcon, Email, Phone, Work, LocationOn, CameraAlt as CameraIcon, Draw as DrawIcon, EventAvailable } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import api from '../../services/api';
import { getImageUrl } from '../../utils/imageService';
import { fetchMyKpiWorksheet, fetchTeamKpiWorksheets } from '../../services/kpiWorksheetService';

const CHAT_SOUND_PREF_PREFIX = 'sgc:chat-sound-enabled:';

const resolveReportingLineId = (reportingLine) => {
  if (!reportingLine) return '';
  if (typeof reportingLine === 'object') {
    return String(reportingLine._id || reportingLine.id || '');
  }
  return String(reportingLine);
};

const formatReportingLineLabel = (emp) => {
  if (!emp) return '';
  const name = [emp.firstName, emp.lastName].filter(Boolean).join(' ') || emp.employeeId || 'Employee';
  return emp.employeeId ? `${name} (${emp.employeeId})` : name;
};

const Profile = () => {
  const showLeaveBalanceSection = false; // Temporarily hidden; keep code for easy re-enable
  const theme = useTheme();
  const { user, updateProfile, changePassword, refreshUser } = useAuth();
  const [profileImage, setProfileImage] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [stampUploading, setStampUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const stampInputRef = useRef(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [chatSoundEnabled, setChatSoundEnabled] = useState(true);
  const [reportingOptions, setReportingOptions] = useState([]);
  const [reportingLineId, setReportingLineId] = useState('');
  const [savingReportingLine, setSavingReportingLine] = useState(false);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [loadingKpiSummary, setLoadingKpiSummary] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);

  useEffect(() => {
    if (user?.profileImage) {
      setProfileImage(getImageUrl(user.profileImage));
    } else {
      setProfileImage(null);
    }
    if (user?.digitalSignature) {
      setSignaturePreview(getImageUrl(user.digitalSignature));
    } else {
      setSignaturePreview(null);
    }
    if (user?.approvalStamp) {
      setStampPreview(getImageUrl(user.approvalStamp));
    } else {
      setStampPreview(null);
    }
  }, [user]);

  useEffect(() => {
    const uid = String(user?.id || user?._id || '');
    if (!uid) return;
    const key = `${CHAT_SOUND_PREF_PREFIX}${uid}`;
    const saved = localStorage.getItem(key);
    setChatSoundEnabled(saved == null ? true : (saved !== '0' && saved !== 'false'));
  }, [user?.id, user?._id]);

  useEffect(() => {
    setReportingLineId(resolveReportingLineId(user?.reportingLine));
  }, [user?.reportingLine]);

  const reportingLineOptions = useMemo(() => {
    const opts = [...reportingOptions];
    const current = user?.reportingLine;
    const currentId = resolveReportingLineId(current);
    if (currentId && !opts.some((e) => String(e._id) === currentId)) {
      opts.unshift({
        _id: currentId,
        firstName: current?.firstName,
        lastName: current?.lastName,
        employeeId: current?.employeeId
      });
    }
    return opts;
  }, [reportingOptions, user?.reportingLine]);

  useEffect(() => {
    const fetchReportingOptions = async () => {
      try {
        const res = await api.get('/auth/reporting-options');
        setReportingOptions(res.data?.data || []);
      } catch (error) {
        setReportingOptions([]);
      }
    };
    fetchReportingOptions();
  }, []);

  useEffect(() => {
    const loadKpiSummary = async () => {
      try {
        setLoadingKpiSummary(true);
        const now = new Date();
        const res = await fetchMyKpiWorksheet({ year: now.getFullYear(), month: now.getMonth() + 1 });
        setKpiSummary(res.data?.data || null);
      } catch (error) {
        setKpiSummary(null);
      } finally {
        setLoadingKpiSummary(false);
      }
    };
    loadKpiSummary();
  }, []);

  useEffect(() => {
    const loadPendingReviews = async () => {
      try {
        const now = new Date();
        const res = await fetchTeamKpiWorksheets({ year: now.getFullYear(), month: now.getMonth() + 1 });
        const teamRows = res.data?.data || [];
        const pending = teamRows.filter((t) => !t.worksheet || !t.worksheet.managerScored).length;
        setPendingReviewCount(pending);
      } catch {
        setPendingReviewCount(0);
      }
    };
    loadPendingReviews();
  }, []);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSnackbar({
        open: true,
        message: 'Please select a valid image file',
        severity: 'error'
      });
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: 'Image size must be less than 5MB',
        severity: 'error'
      });
      return;
    }

    try {
      setUploading(true);

      // Show preview immediately
      const previewUrl = URL.createObjectURL(file);
      setProfileImage(previewUrl);

      // Upload image
      const formData = new FormData();
      formData.append('profileImage', file);

      const response = await api.post('/auth/upload-profile-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const imagePath = response.data.data.imagePath;
        const uploadedUser = response.data.data.user;

        try {
          const refreshResult = await refreshUser();
          const updatedUser = refreshResult?.user || uploadedUser;
          setProfileImage(getImageUrl(updatedUser?.profileImage || imagePath));
        } catch (refreshError) {
          console.error('Error refreshing profile:', refreshError);
          setProfileImage(getImageUrl(uploadedUser?.profileImage || imagePath));
        }

        setSnackbar({
          open: true,
          message: 'Profile image updated successfully!',
          severity: 'success'
        });
      } else {
        throw new Error(response.data.message || 'Image upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      
      // Revert to original image on error
      if (user?.profileImage) {
        setProfileImage(getImageUrl(user.profileImage));
      } else {
        setProfileImage(null);
      }

      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to upload image. Please try again.',
        severity: 'error'
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setSnackbar({ open: true, message: 'New password must be at least 6 characters', severity: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: 'New password and confirmation do not match', severity: 'error' });
      return;
    }
    setPasswordSubmitting(true);
    const result = await changePassword({ currentPassword, newPassword });
    setPasswordSubmitting(false);
    if (result.success) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  const handleSignatureFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Please select an image file (PNG, JPG, etc.)', severity: 'error' });
      return;
    }
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setSnackbar({ open: true, message: 'Signature image must be 2MB or smaller', severity: 'error' });
      return;
    }
    try {
      setSignatureUploading(true);
      const previewUrl = URL.createObjectURL(file);
      setSignaturePreview(previewUrl);
      const response = await authService.uploadDigitalSignature(file);
      if (response.data?.success) {
        await refreshUser();
        setSnackbar({ open: true, message: 'Digital signature saved successfully', severity: 'success' });
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
    } catch (error) {
      if (user?.digitalSignature) {
        setSignaturePreview(getImageUrl(user.digitalSignature));
      } else {
        setSignaturePreview(null);
      }
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to upload signature',
        severity: 'error'
      });
    } finally {
      setSignatureUploading(false);
      if (signatureInputRef.current) {
        signatureInputRef.current.value = '';
      }
    }
  };

  const handleRemoveSignature = async () => {
    try {
      setSignatureUploading(true);
      const result = await updateProfile({ digitalSignature: '' });
      if (result.success) {
        setSignaturePreview(null);
      }
    } finally {
      setSignatureUploading(false);
    }
  };

  const handleToggleChatSound = (enabled) => {
    const uid = String(user?.id || user?._id || '');
    if (!uid) return;
    const key = `${CHAT_SOUND_PREF_PREFIX}${uid}`;
    localStorage.setItem(key, enabled ? '1' : '0');
    setChatSoundEnabled(enabled);
    setSnackbar({
      open: true,
      message: enabled ? 'Chat sound enabled' : 'Chat sound muted',
      severity: 'success'
    });
  };

  const handleSaveReportingLine = async () => {
    try {
      setSavingReportingLine(true);
      await authService.updateProfile({ reportingLineId: reportingLineId || null });
      const refreshed = await refreshUser();
      const savedId = resolveReportingLineId(refreshed?.user?.reportingLine);
      setReportingLineId(savedId);
      setSnackbar({ open: true, message: 'Reporting line updated successfully', severity: 'success' });
    } catch (error) {
      setReportingLineId(resolveReportingLineId(user?.reportingLine));
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update reporting line',
        severity: 'error'
      });
    } finally {
      setSavingReportingLine(false);
    }
  };

  const formatEmployeeId = (employeeId) => {
    if (!employeeId) return 'N/A';
    return employeeId.toString().padStart(5, '0');
  };

  const navigate = useNavigate();

  const handleStampFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Please select an image file for stamp', severity: 'error' });
      return;
    }
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setSnackbar({ open: true, message: 'Approval stamp image must be 2MB or smaller', severity: 'error' });
      return;
    }
    try {
      setStampUploading(true);
      setStampPreview(URL.createObjectURL(file));
      const response = await authService.uploadApprovalStamp(file);
      if (response.data?.success) {
        await refreshUser();
        setSnackbar({ open: true, message: 'Approval stamp saved successfully', severity: 'success' });
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
    } catch (error) {
      if (user?.approvalStamp) {
        setStampPreview(getImageUrl(user.approvalStamp));
      } else {
        setStampPreview(null);
      }
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to upload approval stamp',
        severity: 'error'
      });
    } finally {
      setStampUploading(false);
      if (stampInputRef.current) {
        stampInputRef.current.value = '';
      }
    }
  };

  const handleRemoveStamp = async () => {
    try {
      setStampUploading(true);
      const result = await updateProfile({ approvalStamp: '' });
      if (result.success) {
        setStampPreview(null);
      }
    } finally {
      setStampUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
          User Profile
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/profile/team-kpi-reviews')}
          >
            <Badge
              color="error"
              badgeContent={pendingReviewCount}
              invisible={!pendingReviewCount}
              max={99}
            >
              Team KPI reviews
            </Badge>
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/profile/kpi-sheet')}
          >
            Monthly KPI sheet
          </Button>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        {/* Profile Image and Basic Info */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                <Avatar 
                  src={profileImage}
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    mx: 'auto',
                    border: `4px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }}
                  onClick={handleImageClick}
                >
                  {!profileImage && (
                    <PersonIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />
                  )}
                </Avatar>
                <Tooltip title="Click to upload or change profile image">
                  <IconButton
                    onClick={handleImageClick}
                    disabled={uploading}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      border: `3px solid ${theme.palette.background.paper}`,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {uploading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <CameraIcon />
                    )}
                  </IconButton>
                </Tooltip>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </Box>
              
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                {user?.position || 'Employee'}
              </Typography>
              
              <Chip 
                label={(user?.roleRef?.name || user?.roleRef?.displayName || user?.roles?.[0]?.name || user?.role || 'Employee').toUpperCase()} 
                color="primary" 
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Detailed Information */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.text.primary }}>
                Profile Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Email sx={{ color: theme.palette.primary.main }} />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Email Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user?.email || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Work sx={{ color: theme.palette.primary.main }} />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Employee ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatEmployeeId(user?.employeeId)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Phone sx={{ color: theme.palette.primary.main }} />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user?.phone || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <LocationOn sx={{ color: theme.palette.primary.main }} />
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Department
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user?.department || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Account Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={user?.isActive ? 'Active' : 'Inactive'} 
                      color={user?.isActive ? 'success' : 'error'} 
                      size="small"
                    />
                    {user?.isEmailVerified && (
                      <Chip 
                        label="Email Verified" 
                        color="info" 
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Description Card */}
        {user?.jobDescription && (
          <Grid item xs={12}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Work color="info" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                    Job Description (JD)
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: theme.palette.text.primary, lineHeight: 1.6 }}>
                  {user.jobDescription}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Reporting line
              </Typography>
              {user?.reportingLine && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Assigned: <strong>{formatReportingLineLabel(user.reportingLine)}</strong>
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Reporting line"
                    value={reportingLineId}
                    onChange={(e) => setReportingLineId(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {reportingLineOptions.map((emp) => (
                      <MenuItem key={emp._id} value={String(emp._id)}>
                        {formatReportingLineLabel(emp)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSaveReportingLine}
                    disabled={savingReportingLine}
                  >
                    {savingReportingLine ? <CircularProgress size={22} color="inherit" /> : 'Save reporting line'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.06)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                KPI section
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current month KPI snapshot. Open full sheet for details and history.
              </Typography>
              {loadingKpiSummary ? (
                <CircularProgress size={24} />
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Month</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {kpiSummary?.month ? `${kpiSummary.month}/${kpiSummary.year}` : 'Not available'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Total KPI score</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {kpiSummary?.totalKPIScore ?? '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">Total weight</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {kpiSummary?.totalWeight ?? 0}%
                    </Typography>
                  </Grid>
                </Grid>
              )}
              <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/profile/kpi-sheet')}>
                Open monthly KPI sheet
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Leave Balance Card */}
        {showLeaveBalanceSection && user?.leaveBalance && (
          <Grid item xs={12}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EventAvailable color="success" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                    Leave Balance
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {Object.entries(user.leaveBalance).map(([type, balance]) => {
                    if (!balance || typeof balance !== 'object' || type === '_id' || type === 'medical' || type === 'maternity' || type === 'paternity') {
                      if (balance && balance.allocated > 0) {
                        // Show if explicitly allocated
                      } else {
                        return null;
                      }
                    }
                    return (
                      <Grid item xs={12} sm={4} key={type}>
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2 }}>
                          <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', fontWeight: 'bold', color: theme.palette.success.main, mb: 1 }}>
                            {type} Leave
                          </Typography>
                          <Divider sx={{ mb: 2, opacity: 0.5 }} />
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 'bold', letterSpacing: 0.5 }}>REMAINING</Typography>
                              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{balance.remaining}</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}`, pl: 2 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontWeight: 'bold', letterSpacing: 0.5 }}>USED</Typography>
                              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{balance.used}</Typography>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="textSecondary">Total Quota: {balance.allocated}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                  {Math.round((balance.used / balance.allocated) * 100) || 0}% Used
                                </Typography>
                              </Box>
                              <Box sx={{ width: '100%', height: 6, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 3, overflow: 'hidden' }}>
                                <Box sx={{ 
                                  width: `${Math.min(100, (balance.used / balance.allocated) * 100)}%`, 
                                  height: '100%', 
                                  bgcolor: theme.palette.success.main,
                                  transition: 'width 1s ease-in-out'
                                }} />
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.text.primary }}>
                Change password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use your current password and choose a new one (at least 6 characters).
              </Typography>
              <Box component="form" onSubmit={handleChangePassword}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current password"
                  value={currentPassword}
                  onChange={(ev) => setCurrentPassword(ev.target.value)}
                  margin="normal"
                  autoComplete="current-password"
                />
                <TextField
                  fullWidth
                  type="password"
                  label="New password"
                  value={newPassword}
                  onChange={(ev) => setNewPassword(ev.target.value)}
                  margin="normal"
                  autoComplete="new-password"
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm new password"
                  value={confirmPassword}
                  onChange={(ev) => setConfirmPassword(ev.target.value)}
                  margin="normal"
                  autoComplete="new-password"
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ mt: 2 }}
                  disabled={passwordSubmitting || !currentPassword || !newPassword || !confirmPassword}
                >
                  {passwordSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Update password'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DrawIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                  Approval stamp
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload your official approval stamp. In Pre-Audit, you can choose to apply this stamp on approve/reject actions.
              </Typography>
              <Box
                sx={{
                  border: `1px dashed ${alpha(theme.palette.secondary.main, 0.4)}`,
                  borderRadius: 1,
                  p: 2,
                  minHeight: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.secondary.main, 0.04),
                  mb: 2
                }}
              >
                {stampPreview ? (
                  <Box component="img" src={stampPreview} alt="Your approval stamp" sx={{ maxHeight: 96, maxWidth: '100%', objectFit: 'contain' }} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No approval stamp on file
                  </Typography>
                )}
              </Box>
              <input
                ref={stampInputRef}
                type="file"
                accept="image/*"
                onChange={handleStampFileChange}
                style={{ display: 'none' }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button variant="contained" color="secondary" onClick={() => stampInputRef.current?.click()} disabled={stampUploading}>
                  {stampUploading ? <CircularProgress size={22} color="inherit" /> : 'Upload stamp'}
                </Button>
                {user?.approvalStamp ? (
                  <Button variant="outlined" color="error" onClick={handleRemoveStamp} disabled={stampUploading}>
                    Remove
                  </Button>
                ) : null}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DrawIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                  Digital signature
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a clear image of your signature (PNG or JPG). It can be used on printouts and approvals where your signature is required.
              </Typography>
              <Box
                sx={{
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                  borderRadius: 1,
                  p: 2,
                  minHeight: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  mb: 2
                }}
              >
                {signaturePreview ? (
                  <Box
                    component="img"
                    src={signaturePreview}
                    alt="Your signature"
                    sx={{ maxHeight: 96, maxWidth: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No signature on file
                  </Typography>
                )}
              </Box>
              <input
                ref={signatureInputRef}
                type="file"
                accept="image/*"
                onChange={handleSignatureFileChange}
                style={{ display: 'none' }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => signatureInputRef.current?.click()}
                  disabled={signatureUploading}
                >
                  {signatureUploading ? <CircularProgress size={22} color="inherit" /> : 'Upload signature'}
                </Button>
                {user?.digitalSignature ? (
                  <Button variant="outlined" color="error" onClick={handleRemoveSignature} disabled={signatureUploading}>
                    Remove
                  </Button>
                ) : null}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.text.primary }}>
                Chat preferences
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Control whether an audible alert plays when a new chat message arrives in real time.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={chatSoundEnabled}
                    onChange={(e) => handleToggleChatSound(e.target.checked)}
                    color="primary"
                  />
                }
                label={chatSoundEnabled ? 'Chat sound on' : 'Chat sound off'}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile; 